import React, {useCallback, useEffect, useReducer, useRef, useState} from 'react'
import {useLocation} from 'react-router-dom/cjs/react-router-dom'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {Model} from 'survey-core'
import {Survey} from 'survey-react-ui'
import {Box, Flex, useMultiStyleConfig} from '@chakra-ui/react'
import StickyBar from './partials/sticky-bar'
import HelpMeChooseHeader from './partials/help-me-choose-header'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import {initialState, reducer} from './partials/help-me-choose-reducer'
import SurveyResults from './partials/survey-results'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {help_me_choose_intermediate_event} from '../../custom-api/layers/pages/help-me-choose'
import {basic_gtm_event} from '../../custom-api/layers/default'
import useHelpMeChooseSurvey from '../../custom-hooks/use-help-me-choose-survey'
import {useCustomPreferenceWithLocale} from '../../hooks/use-custom-preference'
import routingObject from '../../utils/static-routes'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Helmet} from 'react-helmet'
import {defineMessage, useIntl} from 'react-intl'

const HelpMeChoose = () => {
    const [survey, setSurvey] = useState(null)
    const [state, dispatch] = useReducer(reducer, initialState)
    const location = useLocation()
    const navigate = useNavigation()
    const {addLayer} = useGoogleTagManager()
    const surveyId = useCustomPreferenceWithLocale('surveyId')
    const {customParams} = getConfig()
    const currentPath = 'https://' + customParams.currentHost + location.pathname
    const intl = useIntl()
    const helpMeChooseHeaderMessage = defineMessage({
        id: 'helpMeChoose.page.header',
        defaultMessage: 'Help me choose',
    })

    const helpMeChooseSubHeaderMessage = defineMessage({
        id: 'helpMeChoose.page.subHeader',
        defaultMessage: '{headerTitle}',
    })
    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: intl.formatMessage(helpMeChooseHeaderMessage),
        description: intl.formatMessage(helpMeChooseSubHeaderMessage, {
            headerTitle: state.headerTitle,
        }),
        url: currentPath,
    }

    const completedProducts = useRef(false)
    const setCompletedProducts = (value) => (completedProducts.current = value)
    const {data: surveyJson} = useHelpMeChooseSurvey(
        {parameters: {id: surveyId}},
        {enabled: !!surveyId},
    )

    const styles = useMultiStyleConfig('HelpMeChoose', {
        isInitialized: survey && Object.keys(survey?.data).length > 0 && survey?.currentPageNo > 0,
    })

    const surveyStyles = useMultiStyleConfig('Survey', {isPageMultiSelect: state.isPageMultiSelect})
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()

    const handleSurveyProcess = useCallback(async (survey, options) => {
        if (survey?.isCompleted && !completedProducts.current) {
            setCompletedProducts(true)
            try {
                const token = await getTokenWhenReady()
                const _retrievedProducts = await api.shopperProducts.getProducts({
                    parameters: {
                        ids: options?.html?.split(',').slice(0, 3).join(','),
                        expand: ['availability', 'images'],
                    },
                    headers: {Authorization: `Bearer ${token}`},
                })
                dispatch({
                    type: 'SET_RETRIEVED_PRODUCTS',
                    payload: _retrievedProducts.data,
                })
                dispatch({
                    type: 'SET_HEADER_TITLE',
                    payload: survey?.localizableStrings?.completeText?.renderedText,
                })
                await addLayer(
                    basic_gtm_event.bind(this, {
                        event: 'help_me_choose',
                        eventCategory: 'engagement',
                        eventAction: 'help_me_choose-end',
                        eventLabel: _retrievedProducts?.data
                            ?.map((product) => product.id)
                            .join(','),
                    }),
                )
            } catch (e) {
                console.error(e)
            }
        } else return
    }, [])

    const handleRenderPage = useCallback(async (survey, options) => {
        dispatch({
            type: 'SET_RENDER_PAGE',
            payload: {
                headerTitle: options?.page?.localizableStrings?.title?.renderedText,
                proceedBtnDisabled: true,
            },
        })
        survey?.currentPage?.questions.forEach((question) => question.clearValue())

        const descriptions = survey?.currentPage?.questions
            ?.filter((question) => question.choices)
            ?.flatMap((q) => q?.jsonObj?.choices)
            ?.map((choice) => choice?.imageDescription)
        const cardTitles = document.querySelectorAll('.sd-imagepicker__text')

        descriptions.forEach((description, i) => {
            if (cardTitles[i] && description) {
                const paragraphNode = document.createElement('div')
                paragraphNode.className = 'sd-imagepicker__description'
                paragraphNode.innerHTML = description
                cardTitles[i].appendChild(paragraphNode)
            }
        })
        const step = survey.currentPageNo - 1
        if (step >= 0) {
            const prevPage = survey.visiblePages[step]
            if (prevPage && prevPage.questions.length > 0) {
                const question = prevPage.questions[0]
                const value = survey.getValue(question.name)
                await addLayer(
                    help_me_choose_intermediate_event.bind(this, {
                        event: 'help_me_choose',
                        eventCategory: 'engagement',
                        eventAction: 'help_me_choose_step',
                        step: step,
                        question: question.title,
                        answers: Array.isArray(value) ? value.join('|') : value,
                    }),
                )
            }
        }
    }, [])

    const handleRenderQuestion = useCallback((survey, options) => {
        dispatch({
            type: 'SET_IS_PAGE_MULTI_SELECT',
            payload: options?.question?.multiSelect,
        })
    }, [])

    const handleValueChange = useCallback(async (survey, options) => {
        if (!options.value || !options.value.length)
            dispatch({type: 'SET_PROCEED_BTN_DISABLED', payload: true})
        else dispatch({type: 'SET_PROCEED_BTN_DISABLED', payload: false})
    }, [])

    const handleOnComplete = useCallback(
        async (survey) => {
            const lastPage = survey.visiblePages[survey.visiblePages.length - 1]
            if (lastPage && lastPage.questions.length > 0) {
                const question = lastPage.questions[0]
                const value = survey.getValue(question.name)
                if (value && (Array.isArray(value) ? value.length > 0 : true)) {
                    await addLayer(
                        help_me_choose_intermediate_event.bind(this, {
                            event: 'help_me_choose',
                            eventCategory: 'engagement',
                            eventAction: 'help_me_choose_step',
                            step: survey.visiblePages.length - 1,
                            question: question.title,
                            answers: Array.isArray(value) ? value.join('|') : value,
                        }),
                    )
                }
            }
            const currentUrl = window && window.location.pathname
            const resultsUrl = `${currentUrl}/results`
            history.pushState({}, '', resultsUrl)
        },
        [addLayer, survey],
    )

    const onRestart = () => {
        survey?.clear()
        survey?.render()
        setCompletedProducts(false)
        navigate(routingObject.HELP_ME_CHOOSE_PATH)
    }

    useEffect(() => {
        const survey = new Model(surveyJson)
        survey?.onAfterRenderPage.add(handleRenderPage)
        survey?.onValueChanged.add(handleValueChange)
        survey?.onAfterRenderQuestion.add(handleRenderQuestion)
        survey?.onProcessHtml.add(handleSurveyProcess)
        survey?.onComplete.add(handleOnComplete)
        setSurvey(survey)
        return () => {
            survey?.onValueChanged.remove(handleValueChange)
            survey?.onAfterRenderPage.remove(handleRenderPage)
            survey?.onAfterRenderQuestion.remove(handleRenderQuestion)
            survey?.onProcessHtml.remove(handleSurveyProcess)
            survey?.onComplete.remove(handleOnComplete)
        }
    }, [surveyJson])

    useEffect(() => {
        if (location.pathname.includes('results') && !survey?.isCompleted)
            navigate(routingObject.HELP_ME_CHOOSE_PATH)
    }, [])

    return (
        survey && (
            <Flex {...styles.mainPageContainer}>
                <Helmet>
                    <script type="application/ld+json">
                        {JSON.stringify(webPageSchemaMarkup, null, 2)}
                    </script>
                </Helmet>
                <Flex {...styles.pageHeaderContainer}>
                    <HelpMeChooseHeader headerTitle={state.headerTitle} />
                </Flex>
                {survey && !survey.isCompleted && (
                    <Box __css={surveyStyles}>
                        <Survey model={survey} goNextPageAutomatic showNavigationButtons={false} />
                    </Box>
                )}
                {survey && survey.isCompleted && (
                    <Flex {...styles.resultsContainer}>
                        <SurveyResults
                            retrievedProducts={state.retrievedProducts}
                            surveyAnswers={survey?.valuesHash}
                        />
                    </Flex>
                )}
                <Flex {...styles.stickyBarContainer} zIndex="1">
                    <StickyBar
                        survey={survey}
                        onRestart={onRestart}
                        proceedBtnDisabled={state.proceedBtnDisabled}
                        isPageMultiSelect={state.isPageMultiSelect}
                    />
                </Flex>
                <Flex {...styles.whitespaceContainer} />
            </Flex>
        )
    )
}

HelpMeChoose.getTemplateName = () => 'help-me-choose'
export default HelpMeChoose
